import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Menu,
  Backdrop,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import projectService, { Offer } from '../../services/projectService';
import { useNotification } from '../layouts/Layout';
import AddOfferForm from './AddOfferForm';

interface OffersTabProps {
  projectId: string;
  projectName: string;
  onCountChange?: (count: number) => void;
}

const OffersTab: React.FC<OffersTabProps> = ({ projectId, projectName, onCountChange }) => {
  // Stan dla ofert
  const [offers, setOffers] = useState<Offer[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  
  // Stan dla formularzy
  const [openAddForm, setOpenAddForm] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  
  // Stan dla menu akcji
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOfferId, setMenuOfferId] = useState<string | null>(null);
  
  // Stan dla ładowania
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();
  
  // Konfiguracja i dostosowanie kolumn – jak w InquiriesTab/PackagesTab
  const headerCellSx: any = React.useMemo(() => ({
    backgroundColor: (t: any) => (t.palette.mode === 'light' ? '#f5f5f5' : 'rgba(255,255,255,0.06)'),
    position: 'relative'
  }), []);
  const [colResizeMode, setColResizeMode] = useState<boolean>(false);
  const [dragCol, setDragCol] = useState<{ key: string; startX: number; startW: number; rightKey?: string; startWRight?: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    id: 160,
    package_name: 240,
    contractor_name: 220,
    offer_type: 120,
    offer_date: 140,
    offered_price: 140,
    actions: 140
  }), []);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(defaultColumnWidths);
  const getWidthNum = React.useCallback((key: string) => columnWidths[key] ?? defaultColumnWidths[key] ?? 40, [columnWidths, defaultColumnWidths]);
  const getColW = React.useCallback((key: string) => `${getWidthNum(key)}px`, [getWidthNum]);
  const ALL_COLUMNS = React.useMemo<string[]>(() => ['id','package_name','contractor_name','offer_type','offer_date','offered_price','actions'], []);
  const STORAGE_PREFIX = 'offersTableCol';
  const VISIBLE_KEY = `${STORAGE_PREFIX}:visible`;
  const ORDER_KEY = `${STORAGE_PREFIX}:order`;
  const WIDTHS_KEY = `${STORAGE_PREFIX}:widths`;
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const ord = JSON.parse(raw);
        if (Array.isArray(ord) && ord.length) return ord.filter((k:string)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!ord.includes(k)));
      }
    } catch {}
    return [...ALL_COLUMNS];
  });
  const [columnsVisible, setColumnsVisible] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') return obj;
      }
    } catch {}
    return {};
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);
  // Panel „Dostosuj kolumny”
  const [colsOpen, setColsOpen] = useState<boolean>(false);
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = useState<string[] | null>(null);
  const [colsPosition, setColsPosition] = useState<{ top:number; left:number }>({ top: 0, left: 0 });
  const [colsDragging, setColsDragging] = useState<{ startX:number; startY:number; top:number; left:number } | null>(null);
  const COLUMNS_DIALOG = { width: 305, height: 420 } as const;
  React.useEffect(() => {
    if (!colsDragging) return;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - (colsDragging?.startX || 0);
      const dy = e.clientY - (colsDragging?.startY || 0);
      const nextTop = Math.max(0, Math.min((window?.innerHeight || 0) - COLUMNS_DIALOG.height, (colsDragging?.top || 0) + dy));
      const nextLeft = Math.max(0, Math.min((window?.innerWidth || 0), (colsDragging?.left || 0) + dx));
      setColsPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => setColsDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [colsDragging]);
  // Persist ustawienia
  React.useEffect(() => { try { localStorage.setItem(ORDER_KEY, JSON.stringify(columnOrder)); } catch {} }, [ORDER_KEY, columnOrder]);
  React.useEffect(() => { try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible)); } catch {} }, [VISIBLE_KEY, columnsVisible]);
  React.useEffect(() => { try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(columnWidths)); } catch {} }, [WIDTHS_KEY, columnWidths]);
  // Obsługa resize kolumn
  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const minW = 24;
      const newW = Math.max(minW, dragCol.startW + dx);
      if (dragCol.rightKey && typeof dragCol.startWRight === 'number') {
        const newRight = Math.max(minW, (dragCol.startWRight as number) - dx);
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW, [dragCol.rightKey as string]: newRight }));
      } else {
        setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
      }
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => { window.removeEventListener('mousemove', onMove); };
  }, [dragCol]);
  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center') => (
    <TableCell align={align} sx={{ ...headerCellSx, width: getColW(keyId), maxWidth: 'none', position: 'relative', userSelect: 'none' }}>
      {keyId === 'id' ? <Box sx={{ pl: '16px' }}>{node}</Box> : node}
      {colResizeMode ? (
        <Box
          onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            const visOrder = (draftOrder ?? columnOrder).filter((k)=> colV(k));
            const idx = visOrder.indexOf(keyId);
            const rightKey = idx >= 0 && idx < visOrder.length - 1 ? visOrder[idx+1] : undefined;
            setDragCol({
              key: keyId,
              startX: e.clientX,
              startW: getWidthNum(keyId),
              rightKey,
              startWRight: rightKey ? getWidthNum(rightKey) : undefined
            });
          }}
          sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }}
        />
      ) : null}
    </TableCell>
  );

  // Sortowanie
  type SortKey = 'id' | 'package_name' | 'contractor_name' | 'offer_type' | 'offer_date' | 'offered_price';
  const [orderBy, setOrderBy] = useState<SortKey>('id');
  const [order, setOrder] = useState<'asc'|'desc'>('asc');
  const handleRequestSort = (key: SortKey) => {
    if (orderBy === key) setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(key); setOrder('asc'); }
  };
  const sortedOffers = React.useMemo(() => {
    const data = [...offers];
    const dir = order === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      const av = (
        orderBy === 'id' ? String(a.offer_id || '') :
        orderBy === 'package_name' ? String(a.package_name || '') :
        orderBy === 'contractor_name' ? String(a.contractor_name || '') :
        orderBy === 'offer_type' ? String(a.offer_type || '') :
        orderBy === 'offer_date' ? String(a.offer_date || '') :
        orderBy === 'offered_price' ? Number(a.offered_price ?? 0) : 0
      );
      const bv = (
        orderBy === 'id' ? String(b.offer_id || '') :
        orderBy === 'package_name' ? String(b.package_name || '') :
        orderBy === 'contractor_name' ? String(b.contractor_name || '') :
        orderBy === 'offer_type' ? String(b.offer_type || '') :
        orderBy === 'offer_date' ? String(b.offer_date || '') :
        orderBy === 'offered_price' ? Number(b.offered_price ?? 0) : 0
      );
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return data;
  }, [offers, orderBy, order]);

  // Pobieranie ofert dla projektu
  useEffect(() => {
    fetchOffers();
  }, [projectId]);
  
  // Obsługa menu akcji
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, offerId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuOfferId(offerId);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOfferId(null);
  };
  
  const fetchOffers = async () => {
    try {
      setLoading(true);
      const data = await projectService.getOffersByProjectId(projectId);
      setOffers(data);
      if (onCountChange) onCountChange(data.length);
    } catch (error) {
      console.error('Błąd podczas pobierania ofert:', error);
      showNotification('Nie udało się pobrać ofert dla projektu', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Obsługa dodawania oferty
  const handleAddOffer = (newOffer: Offer) => {
    const next = [...offers, newOffer];
    setOffers(next);
    if (onCountChange) onCountChange(next.length);
    showNotification('Oferta została dodana pomyślnie', 'success');
  };
  
  // Obsługa edycji oferty
  const handleEditOffer = async (offerId: string, offerData: FormData) => {
    try {
      setLoading(true);
      const updatedOffer = await projectService.updateOffer(offerId, offerData);
      
      // Aktualizuj listę ofert
      setOffers(offers.map(offer => 
        offer.offer_id === offerId ? updatedOffer : offer
      ));
      
      setOpenEditForm(false);
      showNotification('Oferta została zaktualizowana pomyślnie', 'success');
    } catch (error: any) {
      console.error('Błąd podczas aktualizacji oferty:', error);
      showNotification(error.message || 'Wystąpił błąd podczas aktualizacji oferty', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Obsługa usuwania oferty
  const handleDeleteOffer = async () => {
    if (!selectedOffer) return;
    
    try {
      setLoading(true);
      const success = await projectService.deleteOffer(selectedOffer.offer_id);
      
      if (success) {
        // Usuń ofertę z listy
        const next = offers.filter(offer => offer.offer_id !== selectedOffer.offer_id);
        setOffers(next);
        if (onCountChange) onCountChange(next.length);
        setOpenDeleteDialog(false);
        showNotification('Oferta została usunięta pomyślnie', 'success');
      } else {
        showNotification('Nie udało się usunąć oferty', 'error');
      }
    } catch (error: any) {
      console.error('Błąd podczas usuwania oferty:', error);
      showNotification(error.message || 'Wystąpił błąd podczas usuwania oferty', 'error');
    } finally {
      setLoading(false);
      setSelectedOffer(null);
    }
  };
  
  // Renderowanie statusu oferty
  const renderStatus = (status: string) => {
    let color = 'default';
    
    switch (status) {
      case 'Aktywna':
        color = 'primary';
        break;
      case 'Zaakceptowana':
        color = 'success';
        break;
      case 'Odrzucona':
        color = 'error';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        label={status} 
        color={color as 'primary' | 'success' | 'error' | 'default'} 
        size="small" 
        variant="outlined"
      />
    );
  };
  
  // Formatowanie daty
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy', { locale: pl });
    } catch (error) {
      return dateString;
    }
  };
  
  // Formatowanie ceny
  const formatPrice = (price?: number) => {
    if (price === undefined || price === null) return '-';
    return new Intl.NumberFormat('pl-PL', { 
      style: 'currency', 
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };
  
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Pasek akcji – jak w innych zakładkach (pozycja absolutna nad tabelą) */}
      <Box sx={{ position: 'absolute', top: -36, right: 0, display: 'flex', alignItems: 'center', zIndex: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setOpenAddForm(true)} disabled={loading}>
            <Box component="img" src="/assets/icons/add.svg" alt="Dodaj" sx={{ width: 22, height: 22 }} />
          </IconButton>
        </Box>
        <Box sx={{ width: 16 }} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              alignSelf: 'flex-end',
              bgcolor: colResizeMode ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={() => { setColResizeMode(true); setColsOpen(true); setColsPosition({ top: 0, left: 0 }); }}
          >
            <Box
              component="img"
              alt="kolumny"
              src={'/assets/icons/frame-344.svg?v=2'}
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Box>
      </Box>
      
      
      {/* Tabela ofert */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            width: '100%',
            mt: 0,
            height: 'auto',
            overflow: 'auto',
            position: 'relative',
            border: '1px solid #d0d0d6',
            borderBottom: '1px solid #d0d0d6',
            borderRadius: 0,
            borderTopRightRadius: 4,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            overflowX: 'hidden',
            overflowY: 'auto',
            boxShadow: 'none',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { width: 0, height: 0 }
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
              '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
              '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
              '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
            }}
          >
            {(() => {
              const ord = (draftOrder ?? columnOrder);
              return (
                <colgroup>
                  {ord.filter((k)=>k!=='actions').map((key) => (
                    colV(key) ? <col key={`col-${key}`} style={{ width: getColW(key) }} /> : null
                  ))}
                  {colV('actions') ? <col key="col-actions" style={{ width: getColW('actions') }} /> : null}
                </colgroup>
              );
            })()}
            <TableHead sx={{ backgroundColor: (t)=> t.palette.mode==='light' ? undefined : 'rgba(255,255,255,0.06)' }}>
              <TableRow>
                {(draftOrder ?? columnOrder).filter((k)=>k !== 'actions').map((key) => colV(key) ? renderResizableTh(key, (
                  key === 'id' ? (
                    <TableSortLabel active={orderBy==='id'} direction={orderBy==='id'?order:'asc'} onClick={()=>handleRequestSort('id')}>
                      ID
                    </TableSortLabel>
                  ) : key === 'package_name' ? (
                    <TableSortLabel active={orderBy==='package_name'} direction={orderBy==='package_name'?order:'asc'} onClick={()=>handleRequestSort('package_name')}>
                      Nazwa pakietu
                    </TableSortLabel>
                  ) : key === 'contractor_name' ? (
                    <TableSortLabel active={orderBy==='contractor_name'} direction={orderBy==='contractor_name'?order:'asc'} onClick={()=>handleRequestSort('contractor_name')}>
                      Kontrahent
                    </TableSortLabel>
                  ) : key === 'offer_type' ? (
                    <TableSortLabel active={orderBy==='offer_type'} direction={orderBy==='offer_type'?order:'asc'} onClick={()=>handleRequestSort('offer_type')}>
                      Rodzaj
                    </TableSortLabel>
                  ) : key === 'offer_date' ? (
                    <TableSortLabel active={orderBy==='offer_date'} direction={orderBy==='offer_date'?order:'asc'} onClick={()=>handleRequestSort('offer_date')}>
                      Data oferty
                    </TableSortLabel>
                  ) : key === 'offered_price' ? (
                    <TableSortLabel active={orderBy==='offered_price'} direction={orderBy==='offered_price'?order:'asc'} onClick={()=>handleRequestSort('offered_price')}>
                      Cena
                    </TableSortLabel>
                  ) : (
                    ''
                  )
                ), key === 'offered_price' ? 'right' : undefined) : null)}
                {colV('actions') ? renderResizableTh('actions', 'Akcje', 'left') : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedOffers.map((offer) => (
                <TableRow key={offer.offer_id} hover>
                  {colV('id') ? (
                    <TableCell sx={{ width: getColW('id') }}>
                      <Box sx={{ pl: '16px' }}>{offer.offer_id}</Box>
                    </TableCell>
                  ) : null}
                  {colV('package_name') ? (
                    <TableCell sx={{ width: getColW('package_name') }}>
                      <Box sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }}>
                        {offer.package_name}
                      </Box>
                    </TableCell>
                  ) : null}
                  {colV('contractor_name') ? (<TableCell sx={{ width: getColW('contractor_name') }}>{offer.contractor_name}</TableCell>) : null}
                  {colV('offer_type') ? (<TableCell sx={{ width: getColW('offer_type') }}>{offer.offer_type}</TableCell>) : null}
                  {colV('offer_date') ? (<TableCell sx={{ width: getColW('offer_date') }}>{offer.offer_date ? formatDate(offer.offer_date) : '-'}</TableCell>) : null}
                  {colV('offered_price') ? (<TableCell align="right" sx={{ width: getColW('offered_price') }}>{formatPrice(offer.offered_price)}</TableCell>) : null}
                  {colV('actions') ? (
                    <TableCell align="left" sx={{ width: getColW('actions') }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedOffer(offer);
                          setOpenDetailsDialog(true);
                        }}
                        aria-label="szczegóły oferty"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (offer.documentation_link) {
                            window.open(offer.documentation_link, '_blank');
                          } else {
                            showNotification('Link do folderu oferty nie jest dostępny', 'error');
                          }
                        }}
                        aria-label="link do oferty"
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(event) => handleOpenMenu(event, offer.offer_id)}
                        aria-label="więcej"
                        sx={{ ml: 0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Formularz dodawania oferty */}
      <AddOfferForm
        open={openAddForm}
        onClose={() => setOpenAddForm(false)}
        projectId={projectId}
        projectName={projectName}
        onAdd={handleAddOffer}
      />
      
      {/* Formularz edycji oferty */}
      {editingOffer && (
        <AddOfferForm
          open={openEditForm}
          onClose={() => {
            setOpenEditForm(false);
            setEditingOffer(null);
          }}
          projectId={projectId}
          projectName={projectName}
          onAdd={handleAddOffer}
          isEditMode={true}
          initialData={editingOffer}
          onEdit={(offerId, formData) => handleEditOffer(offerId, formData)}
        />
      )}
      
      {/* Dialog usuwania oferty */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz usunąć ofertę "{selectedOffer?.package_name ?? ''}" od {selectedOffer?.contractor_name ?? ''}?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Tej operacji nie można cofnąć.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            color="inherit"
          >
            Anuluj
          </Button>
          <Button 
            onClick={handleDeleteOffer}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Usuwanie...' : 'Usuń'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Okno „Dostosuj kolumny” */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)' }} onClick={()=> setColsOpen(false)}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: `${COLUMNS_DIALOG.height}px`,
            width: `${COLUMNS_DIALOG.width}px`,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            top: colsPosition.top ? `${colsPosition.top}px` : undefined,
            left: colsPosition.left ? `${colsPosition.left}px` : undefined,
            pointerEvents: 'auto'
          }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1, cursor: 'move', userSelect: 'none' }}
            onMouseDown={(e)=> setColsDragging({ startX: e.clientX, startY: e.clientY, top: colsPosition.top, left: colsPosition.left })}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const ord = draftOrder ?? columnOrder;
              if (draftOrder == null) setDraftOrder([...columnOrder]);
              return ord.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e)=>{ e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e)=>{ e.preventDefault(); }}
                  onDrop={(e)=>{ e.preventDefault(); const src = e.dataTransfer.getData('text/plain'); if (!src || src===key) return; setDraftOrder(prev=>{ const cur = [...(prev||ord)]; const from = cur.indexOf(src); const to = cur.indexOf(key); if (from<0||to<0) return cur; cur.splice(to,0,cur.splice(from,1)[0]); return cur; }); }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible(prev=>({ ...(prev||{}), [key]: !((prev||list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {key === 'id' ? 'ID' :
                         key === 'package_name' ? 'Nazwa pakietu' :
                         key === 'contractor_name' ? 'Kontrahent' :
                         key === 'offer_type' ? 'Rodzaj' :
                         key === 'offer_date' ? 'Data oferty' :
                         key === 'offered_price' ? 'Cena' :
                         key.toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicatorIcon fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsOpen(false); setColResizeMode(false); setDraftColumnsVisible(null); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ if (draftColumnsVisible) { setColumnsVisible(draftColumnsVisible); try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(draftColumnsVisible)); } catch {} } if (draftOrder) { const normalized = draftOrder.filter((k)=>ALL_COLUMNS.includes(k)).concat(ALL_COLUMNS.filter(k=>!draftOrder.includes(k))); setColumnOrder(normalized); try { localStorage.setItem(ORDER_KEY, JSON.stringify(normalized)); } catch {} } setColsOpen(false); setColResizeMode(false); }}>
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>
      
      {/* Menu akcji */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          const offer = offers.find(o => o.offer_id === menuOfferId);
          if (offer) {
            setSelectedOffer(offer);
            setEditingOffer(offer);
            setOpenEditForm(true);
          }
          handleCloseMenu();
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edytuj
          </Box>
        </MenuItem>
        <MenuItem onClick={() => {
          const offer = offers.find(o => o.offer_id === menuOfferId);
          if (offer) {
            setSelectedOffer(offer);
            setOpenDeleteDialog(true);
          }
          handleCloseMenu();
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            Usuń
          </Box>
        </MenuItem>
      </Menu>
      
      {/* Dialog szczegółów oferty */}
      {selectedOffer && (
        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#004273', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            Szczegóły oferty
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Nazwa pakietu</Typography>
                <Typography variant="body1">{selectedOffer.package_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Kontrahent</Typography>
                <Typography variant="body1">{selectedOffer.contractor_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Email kontrahenta</Typography>
                <Typography variant="body1">{selectedOffer.contractor_email || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Rodzaj oferty</Typography>
                <Typography variant="body1">{selectedOffer.offer_type}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Data oferty</Typography>
                <Typography variant="body1">{selectedOffer.offer_date ? formatDate(selectedOffer.offer_date) : '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Cena</Typography>
                <Typography variant="body1">{formatPrice(selectedOffer.offered_price)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Typography variant="body1">{renderStatus(selectedOffer.status)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Opiekun</Typography>
                <Typography variant="body1">{selectedOffer.supervisor || '-'}</Typography>
              </Box>
              <Box sx={{ gridColumn: '1 / span 2' }}>
                <Typography variant="subtitle2" color="textSecondary">Opis</Typography>
                <Typography variant="body1">{selectedOffer.description || '-'}</Typography>
              </Box>
              {selectedOffer.documentation_link && (
                <Box sx={{ gridColumn: '1 / span 2' }}>
                  <Typography variant="subtitle2" color="textSecondary">Link do dokumentacji</Typography>
                  <Button
                    variant="text"
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(selectedOffer.documentation_link, '_blank')}
                    sx={{ textTransform: 'none', pl: 0 }}
                  >
                    Otwórz dokumentację
                  </Button>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetailsDialog(false)}>Zamknij</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default OffersTab;
